import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Send, ShieldAlert, Sparkles, CalendarDays, Lock } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { AdvisorMarkdown } from "@/components/portal/Primitives";
import { portalApi, streamAdvisorReply } from "@/lib/portalApi";
import { BEHAVIOR_LABELS, TRAIT_LABELS, DARK_TRIAD_TRAITS } from "@/data/questionnaires";

const SUGGESTIONS = [
  "What do I say in tomorrow's 1:1?",
  "How do I document this properly?",
  "Should I involve HR yet?",
];

export default function IncidentDetailPage() {
  const { id } = useParams();
  const [incident, setIncident] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const openedRef = useRef(false);
  const endRef = useRef(null);

  const scrollToEnd = () => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });

  const run = useCallback(
    async (content) => {
      setBusy(true);
      setStreaming("");
      let acc = "";
      try {
        await streamAdvisorReply(id, content, (delta) => {
          acc += delta;
          setStreaming(acc);
          scrollToEnd();
        });
        setMessages(await portalApi.messages(id));
      } catch (err) {
        toast.error(err.message);
      } finally {
        setStreaming("");
        setBusy(false);
        setTimeout(scrollToEnd, 100);
      }
    },
    [id]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [inc, msgs, access] = await Promise.all([
          portalApi.incident(id),
          portalApi.messages(id),
          portalApi.aiAccess(id).catch(() => ({ allowed: true })),
        ]);
        setIncident(inc);
        setMessages(msgs);
        setLocked(!access.allowed);
        if (access.allowed && !msgs.some((m) => m.role === "assistant") && !openedRef.current) {
          openedRef.current = true;
          run(null);
        }
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, run]);

  const send = (text) => {
    if (locked) return;
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { message_id: `local_${Date.now()}`, role: "user", content, created_at: new Date().toISOString() },
    ]);
    setTimeout(scrollToEnd, 50);
    run(content);
  };

  if (loading) {
    return (
      <PortalLayout back="/portal">
        <div className="flex justify-center py-24" data-testid="incident-loading">
          <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
        </div>
      </PortalLayout>
    );
  }

  if (!incident) {
    return (
      <PortalLayout back="/portal">
        <p className="text-slate-400" data-testid="incident-not-found">This incident could not be found.</p>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout back="/portal">
      <div className="grid lg:grid-cols-[320px_1fr] gap-8 items-start" data-testid="incident-detail">
        <aside className="space-y-5 lg:sticky lg:top-28">
          <div className="border border-slate-800 bg-[#0B1220]/80 p-6">
            <p className="text-[#D4AF37] text-[11px] uppercase tracking-[0.24em] mb-4">Incident</p>
            <p className="flex items-center gap-2 text-slate-500 text-xs mb-4">
              <CalendarDays size={12} />
              {new Date(incident.incident_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
            <div className="flex flex-wrap gap-2 mb-5">
              {incident.behaviors.map((b) => (
                <span key={b} className="text-[10px] uppercase tracking-widest border border-slate-700 text-slate-400 px-2 py-1">
                  {BEHAVIOR_LABELS[b] || b}
                </span>
              ))}
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">{incident.description}</p>
          </div>

          <div className="border border-slate-800 bg-[#0B1220]/80 p-6" data-testid="incident-d3-panel">
            <div className="flex items-center gap-2 mb-4">
              <ShieldAlert size={14} className="text-[#D4AF37]" />
              <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400">Leader D3 profile</p>
            </div>
            {incident.dark_triad_scores ? (
              <>
                <div className="space-y-3 mb-5">
                  {DARK_TRIAD_TRAITS.map((trait) => (
                    <div key={trait} data-testid={`incident-d3-${trait}`}>
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-slate-400">{TRAIT_LABELS[trait]}</span>
                        <span className="font-mono text-[#D4AF37]">{incident.dark_triad_scores[trait]}%</span>
                      </div>
                      <div className="h-[4px] bg-slate-800">
                        <div className="h-full bg-[#D4AF37]" style={{ width: `${incident.dark_triad_scores[trait]}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to={`/portal/report`}
                  className="text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] hover:underline"
                  data-testid="incident-to-report-link"
                >
                  Open vulnerability report →
                </Link>
              </>
            ) : (
              <>
                <p className="text-slate-400 text-sm leading-relaxed mb-5">
                  Rate 15 observable behaviours to place this leader on the dark triad. It sharpens every answer your advisor gives.
                </p>
                <Link
                  to={`/portal/incident/${id}/leader-d3`}
                  data-testid="incident-start-d3-btn"
                  className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors"
                >
                  Run assessment
                </Link>
              </>
            )}
          </div>
        </aside>

        <section>
          <div className="flex items-center gap-2 mb-6">
            <Sparkles size={16} className="text-[#D4AF37]" />
            <h2 className="font-serif text-xl md:text-2xl text-white">Your AI advisor</h2>
          </div>

          <div className="space-y-4" data-testid="advisor-thread">
            {messages.map((m) => (
              <div
                key={m.message_id}
                data-testid={`advisor-message-${m.role}`}
                className={
                  m.role === "user"
                    ? "ml-auto max-w-[85%] border border-slate-700 bg-slate-900/70 px-5 py-4"
                    : "max-w-full border border-slate-800 bg-[#0B1220]/80 px-5 py-5 md:px-7 md:py-6"
                }
              >
                {m.role === "user" ? (
                  <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">{m.content}</p>
                ) : (
                  <AdvisorMarkdown>{m.content}</AdvisorMarkdown>
                )}
              </div>
            ))}

            {streaming && (
              <div className="border border-[#D4AF37]/30 bg-[#0B1220]/80 px-5 py-5 md:px-7 md:py-6" data-testid="advisor-streaming">
                <AdvisorMarkdown>{streaming}</AdvisorMarkdown>
              </div>
            )}
            {busy && !streaming && (
              <div className="border border-slate-800 bg-[#0B1220]/60 px-5 py-5 flex items-center gap-3" data-testid="advisor-thinking">
                <Loader2 className="animate-spin text-[#D4AF37]" size={15} />
                <span className="text-slate-400 text-sm">Reading the pattern in your incident…</span>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {!busy && !locked && (
            <div className="flex flex-wrap gap-2 mt-6" data-testid="advisor-suggestions">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="border border-slate-800 text-slate-400 px-4 py-2 text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {locked ? (
            <div
              className="mt-6 border border-[#D4AF37]/40 bg-[#0B1220]/80 p-6 md:p-8"
              data-testid="advisor-locked"
            >
              <p className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] mb-3 flex items-center gap-2">
                <Lock size={13} /> Premium feature
              </p>
              <h3 className="font-serif text-lg md:text-xl text-white mb-3">
                Your free advisor conversation is already open on another incident
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed max-w-2xl mb-6">
                The free plan includes one full conversation with the AI advisor. You can keep reading everything you
                have already discussed — Premium unlocks unlimited advisor conversations on every incident you log,
                plus your full vulnerability report with counter-strategies.
              </p>
              <Link
                to="/portal/pricing"
                data-testid="advisor-upgrade-link"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
              >
                Unlock unlimited conversations
              </Link>
            </div>
          ) : (
            <div className="mt-6 flex gap-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={busy}
              data-testid="advisor-input"
              placeholder="Ask your advisor anything about this situation…"
              className="flex-1 bg-[#0B1220] border border-slate-800 px-4 py-3.5 text-white text-sm placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none transition-colors disabled:opacity-60"
            />
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              data-testid="advisor-send-btn"
              className="bg-[#D4AF37] text-slate-950 px-6 hover:bg-[#B5952F] disabled:opacity-40 transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
          )}
        </section>
      </div>
    </PortalLayout>
  );
}
